import { countryLocaleMap } from '~/constants/config/country-locale-map';

/**
 * Resolve the BCP-47 locale tag for a country code.
 * Falls back to 'en-US' if the country is not in the map.
 */
function localeForCountry(countryCode: string): string {
  return countryLocaleMap[countryCode]?.locale ?? 'en-US';
}

/**
 * Resolve the ISO 4217 currency code for a country code.
 * Falls back to 'USD' if the country is not in the map.
 */
function currencyForCountry(countryCode: string): string {
  return countryLocaleMap[countryCode]?.currency ?? 'USD';
}

/**
 * Format a number as a currency string for the given country.
 * @example formatCurrency(1234.56, 'US') => '$1,234.56'
 * @example formatCurrency(1234.56, 'DE') => '1.234,56\u00a0€'
 */
export function formatCurrency(amount: number, countryCode: string): string {
  const locale = localeForCountry(countryCode);
  const currency = currencyForCountry(countryCode);

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Format a number using the locale conventions of the given country.
 * @example formatNumber(1234567.89, 'US') => '1,234,567.89'
 * @example formatNumber(1234567.89, 'DE') => '1.234.567,89'
 */
export function formatNumber(value: number, countryCode: string): string {
  const locale = localeForCountry(countryCode);

  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Compact human-readable byte size, e.g. `1.4 MB`. Bytes render whole; larger units get
 * one decimal. Non-positive input is `0 B` rather than `NaN`.
 */
export function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${i === 0 ? String(value) : value.toFixed(1)} ${units[i] ?? 'B'}`;
}

/**
 * Compact human-readable count, e.g. `84K`, `1.5M`. Locale-agnostic (unlike
 * `formatNumber`) — used for large search-volume style numbers where a fixed
 * K/M/B suffix reads better than full locale-formatted digits.
 * @example formatCompactNumber(84000) => '84K'
 * @example formatCompactNumber(1500000) => '1.5M'
 */
export function formatCompactNumber(n: number): string {
  if (n >= 1_000_000_000) {
    const v = n / 1_000_000_000;
    return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}B`;
  }
  if (n >= 1_000_000) {
    const v = n / 1_000_000;
    return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}M`;
  }
  if (n >= 1_000) {
    const v = n / 1_000;
    return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}K`;
  }
  return String(n);
}
